import { ReactNode } from 'react';
import parse, {
  domToReact,
  Element,
  DOMNode,
  HTMLReactParserOptions,
} from 'html-react-parser';

import { resolveNotificationLink } from './notifications';

/**
 * Campaign messages may carry a tiny amount of operator-authored markup: bold,
 * italic, link, line break. This renders it.
 *
 * WHY THIS IS NOT dangerouslySetInnerHTML: `html-react-parser` builds React elements
 * rather than injecting a string, so a `<script>` becomes an element React never
 * executes, and an inline `onerror` becomes a string prop React ignores because it
 * only accepts function handlers. What it does NOT stop is `href="javascript:..."`,
 * which survives as a prop — React 18 warns but that is not something to rely on.
 * Hence every href goes through resolveNotificationLink, the same allowlist used for
 * notification navigation.
 *
 * amlbot sanitises to this same tag set on save. This is the second gate, and it
 * matters independently: it also covers rows written before that sanitiser existed,
 * and anything that reaches the table by another route.
 */

// Mirrors Web\Service\Campaign::ALLOWED_TAGS in amlbot.
const ALLOWED_TAGS = new Set(['b', 'strong', 'i', 'em', 'a', 'br']);

interface MarkupOptions {
  // Called when the customer activates a link inside the message, with that link's
  // resolved href. The caller marks the notification read and performs the navigation.
  //
  // Both halves matter, and both were learned the hard way end-to-end:
  //
  // 1. EXPLICIT callback, not event bubbling. Activating the link closes the popover,
  //    which unmounts the row before React's delegated handler processes the bubble,
  //    so a handler on the row never ran.
  // 2. The caller must NAVIGATE too, because a raw <a href="/x"> triggers a full
  //    document load in this SPA. That tears down the page and aborts the in-flight
  //    mark-read request — the badge stayed stuck on a campaign the customer had
  //    demonstrably just read. Internal links therefore preventDefault here and are
  //    routed through react-router by the caller.
  //
  // External links are left to the browser (target=_blank opens a new tab, so this
  // document survives and the request completes).
  onLinkActivate?: (href: string, external: boolean) => void;
}

/**
 * Exported for the parser-parity tests, not for callers — use renderMessageMarkup.
 *
 * html-react-parser's `parse(html, options)` is exactly
 * `domToReact(htmlToDOM(html), options)`, so a test that pairs these options with a
 * DOM from a DIFFERENT html-dom-parser build reproduces the shipping pipeline with
 * the shipping tree-construction. That matters because Vitest and the bundle resolve
 * html-dom-parser differently — see the parser-parity block in the test file.
 */
export const buildOptions = (opts: MarkupOptions): HTMLReactParserOptions => {
  const options: HTMLReactParserOptions = {
    replace: (node) => {
      if (!(node instanceof Element)) return undefined;

      const tag = node.name?.toLowerCase();

      // Anything outside the allowlist keeps its words and loses its markup, matching
      // the server's unwrap-don't-delete behaviour.
      if (!tag || !ALLOWED_TAGS.has(tag)) {
        return <>{domToReact(node.children as DOMNode[], options)}</>;
      }

      if (tag === 'a') {
        const resolved = resolveNotificationLink(node.attribs?.href);
        const children = domToReact(node.children as DOMNode[], options);

        // An unusable scheme keeps the operator's wording but is not a link. Rendering
        // it as an anchor with no href would look clickable and do nothing.
        if (resolved.kind === 'rejected') {
          return <>{children}</>;
        }

        // No stopPropagation, and no reliance on propagation either — see the note on
        // MarkupOptions.onLinkActivate. The callback marks the item read directly; the
        // row separately detects a link-originated click and skips its own navigation
        // so the two never race.
        return (
          <a
            href={resolved.href}
            onClick={(e) => {
              // Internal: stop the browser doing a full document load, and let the
              // caller route it. External: let the browser open the new tab.
              if (resolved.kind === 'internal') e.preventDefault();
              opts.onLinkActivate?.(
                resolved.href,
                resolved.kind === 'external',
              );
            }}
            {...(resolved.kind === 'external'
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {children}
          </a>
        );
      }

      // Strip every attribute from the remaining formatting tags. The server already
      // does this, but a class or style arriving from anywhere else must not be honoured.
      if (tag === 'br') return <br />;
      if (tag === 'b' || tag === 'strong') {
        return (
          <strong>{domToReact(node.children as DOMNode[], options)}</strong>
        );
      }
      return <em>{domToReact(node.children as DOMNode[], options)}</em>;
    },
  };

  return options;
};

export const renderMessageMarkup = (
  message: string,
  opts: MarkupOptions = {},
): ReactNode => {
  if (!message) return null;
  return parse(message, buildOptions(opts));
};
